/**
 * Pricing & access (section 2): what the brand sells (its store products, from GET
 * /admin/v1/products), then the ways in that are not bought in an app: ticket codes, guest lists and free
 * access for one person. Products are picked from what is sold, never typed, unless the access is
 * for something sold elsewhere (a season pass, a festival ticket).
 */

import { useCallback, useEffect, useState } from "react";
import Button from "@web-ui/components/Button";
import Chip from "@web-ui/components/Chip";
import DataTable from "@web-ui/components/DataTable";
import DataTableRow from "@web-ui/components/DataTableRow";
import Empty from "@web-ui/components/Empty";
import Field from "@web-ui/components/Field";
import ListRow from "@web-ui/components/ListRow";
import Notice from "@web-ui/components/Notice";
import Select from "@web-ui/components/Select";
import { grants, type Grant, type ProductOffer, type VoucherBatch } from "@/api/admin";
import { ApiError } from "@/api/client";
import { productLabel } from "@/forms/access";
import { dateTime, relative } from "@/lib/format";
import { useStudio } from "@/shell/studio";
import { TERMS } from "@/vocabulary/vocabulary";

type Product = ProductOffer;

export function Pricing() {
  const { client, products: offered } = useStudio();
  const products = offered ?? [];
  return (
    <>
      <section className="card flex flex-col gap-3" aria-labelledby="sold">
        <div className="ps-card-inset">
          <h2 id="sold" className="text-sub-title-1">What you sell</h2>
          <p className="text-body-2 text-t-secondary">Sold in the App Store, Google Play and Amazon. Prices are set in each store; ask ViewForm to add or change a product.</p>
        </div>
        {offered === null ? <p className="text-body-2 text-t-secondary ps-card-inset">Loading what you sell…</p> : products.length ? (
          <div className="flex flex-col">
            {products.map((p) => (
              <ListRow key={p.productId} icon={p.kind === "subscription" ? "star-stroke" : p.kind === "rental" ? "clock" : "bag"} title={productLabel(p, client.brand.id)}
                detail={p.kind === "subscription" ? "Watch everything members can watch, for as long as they pay." : p.kind === "rental" ? "Watch one item for a limited time." : "Keep one item for good."}
                meta={<span className="font-mono text-caption text-t-tertiary">{p.productId}</span>} />
            ))}
          </div>
        ) : <Empty icon="wallet" title="Nothing is sold yet" description="Everything in the app is free to watch. Ask ViewForm to set up memberships, rentals or purchases." />}
      </section>
      <CodesAndGuests products={products} />
    </>
  );
}

const OTHER = -1;

/** Pick what the access unlocks: one of the products sold, or another product id (a pass sold elsewhere). */
function ProductPicker({ label, products, value, onChange }: { label: string; products: Product[]; value: string; onChange: (id: string) => void }) {
  const { client } = useStudio();
  const [other, setOther] = useState(!products.length);
  const options = [...products.map((p, i) => ({ id: i, name: productLabel(p, client.brand.id) })), { id: OTHER, name: "Something sold elsewhere…" }];
  const chosen = other ? options.at(-1)! : options.find((o) => o.id >= 0 && products[o.id]!.productId === value) ?? null;
  return (
    <div className="flex flex-col gap-3">
      {products.length ? <Select label={label} placeholder="Choose" value={chosen} options={options} onChange={(o) => { if (o.id === OTHER) { setOther(true); onChange(""); } else { setOther(false); onChange(products[o.id]!.productId); } }} /> : null}
      {other ? <Field label={products.length ? "Its product id" : label} description="As your ticket shop or pass names it, e.g. encore.ticket.lanterns" value={value} onChange={(e) => onChange(e.target.value.trim())} /> : null}
    </div>
  );
}

function CodesAndGuests({ products }: { products: Product[] }) {
  const { client, canEdit } = useStudio();
  const [batches, setBatches] = useState<VoucherBatch[]>([]);
  const [list, setList] = useState<Grant[]>([]);
  const [codes, setCodes] = useState<{ label: string; codes: string[] } | null>(null);
  const [form, setForm] = useState({ label: "", productId: "", count: "50", maxRedemptions: "1", grantDays: "" });
  const [guests, setGuests] = useState({ productId: "", emails: "" });
  const [comp, setComp] = useState({ email: "", productId: "", days: "" });
  const [message, setMessage] = useState<{ tone: "positive" | "critical"; text: string } | null>(null);
  const load = useCallback(async () => {
    const [b, g] = await Promise.all([grants.batches(client), grants.list(client)]);
    setBatches(b.batches); setList(g.grants);
  }, [client]);
  useEffect(() => { void load().catch(() => setMessage({ tone: "critical", text: "Codes and guest lists did not load." })); }, [load]);
  const fail = (e: unknown) => setMessage({ tone: "critical", text: e instanceof ApiError ? String(e.body["issue"] ?? e.code) : "That did not go through." });
  const nameOf = (id: string) => { const p = products.find((x) => x.productId === id); return p ? productLabel(p, client.brand.id).split(" · ")[0]! : id; };

  const createBatch = async () => {
    try {
      const out = await grants.createBatch(client, { label: form.label, productId: form.productId, count: Number(form.count), maxRedemptions: Number(form.maxRedemptions) || 1, grantDays: form.grantDays ? Number(form.grantDays) : null });
      setCodes({ label: form.label, codes: out.codes }); await load();
    } catch (e) { fail(e); }
  };
  const download = () => {
    if (!codes) return;
    const blob = new Blob([`code\n${codes.codes.join("\n")}\n`], { type: "text/csv" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `${codes.label.replace(/[^\w-]+/g, "-")}-codes.csv` });
    a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <>
      <div className="ps-card-inset pt-3">
        <h2 className="text-sub-title-1">{TERMS.grants}</h2>
        <p className="text-body-2 text-t-secondary">Access that is not bought in the app: tickets sold at the door, a list of invited guests, a free pass for one person.</p>
      </div>
      {message ? <Notice tone={message.tone} onDismiss={() => setMessage(null)}>{message.text}</Notice> : null}
      {codes ? (
        <Notice tone="warning" title={`${codes.codes.length} codes for “${codes.label}”: shown this once`} action={<div className="flex flex-wrap gap-2"><Button variant="primary" type="button" size="sm" onClick={download}>Download the codes</Button><Button type="button" size="sm" variant="ghost" onClick={() => setCodes(null)}>I have saved them</Button></div>}>
          They can't be shown again, so save them now. <span className="font-mono">{codes.codes.slice(0, 3).join(" · ")}{codes.codes.length > 3 ? " …" : ""}</span>
        </Notice>
      ) : null}
      {canEdit ? (
        <div className="grid gap-3 lg:grid-cols-3">
          <section className="card flex flex-col gap-3">
            <h3 className="text-sub-title-1 ps-card-inset">Ticket codes</h3>
            <p className="text-body-2 text-t-secondary ps-card-inset">Codes viewers type in the app to unlock something.</p>
            <Field label="Name for this batch" placeholder="e.g. Opening night" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            <ProductPicker label="What a code unlocks" products={products} value={form.productId} onChange={(productId) => setForm({ ...form, productId })} />
            <div className="grid grid-cols-3 gap-2">
              <Field label="Codes" type="number" min={1} max={500} value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} />
              <Field label="Uses each" type="number" min={1} value={form.maxRedemptions} onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })} />
              <Field label="Days" type="number" min={1} optional value={form.grantDays} onChange={(e) => setForm({ ...form, grantDays: e.target.value })} />
            </div>
            <Button variant="secondary" type="button" disabled={!form.label || !form.productId} onClick={() => void createBatch()}>Make codes</Button>
          </section>
          <section className="card flex flex-col gap-3">
            <h3 className="text-sub-title-1 ps-card-inset">Guest list</h3>
            <p className="text-body-2 text-t-secondary ps-card-inset">People who bought a ticket elsewhere. They get access as soon as they sign in with this address.</p>
            <ProductPicker label="What they get" products={products} value={guests.productId} onChange={(productId) => setGuests({ ...guests, productId })} />
            <Field label="Email addresses" textarea description="One per line, or separated by commas." value={guests.emails} onChange={(e) => setGuests({ ...guests, emails: e.target.value })} />
            <Button variant="secondary" type="button" disabled={!guests.productId || !guests.emails.trim()} onClick={() => void grants.addGuests(client, guests.productId, guests.emails.split(/[\s,;]+/).filter(Boolean))
              .then((r) => { setMessage({ tone: "positive", text: `${r.added} added, ${r.duplicates} already on the list${r.invalid.length ? `, ${r.invalid.length} not email addresses` : ""}.` }); setGuests({ ...guests, emails: "" }); return load(); }).catch(fail)}>Add to the guest list</Button>
          </section>
          <section className="card flex flex-col gap-3">
            <h3 className="text-sub-title-1 ps-card-inset">Free access</h3>
            <p className="text-body-2 text-t-secondary ps-card-inset">For one person: press, partners, testers.</p>
            <Field label="Email" type="email" value={comp.email} onChange={(e) => setComp({ ...comp, email: e.target.value })} />
            <ProductPicker label="What they get" products={products} value={comp.productId} onChange={(productId) => setComp({ ...comp, productId })} />
            <Field label="Days" type="number" min={1} optional value={comp.days} onChange={(e) => setComp({ ...comp, days: e.target.value })} />
            <Button variant="secondary" type="button" disabled={!comp.email || !comp.productId} onClick={() => void grants.comp(client, comp.email, comp.productId, comp.days ? Number(comp.days) : null)
              .then(() => { setMessage({ tone: "positive", text: `${comp.email} has ${nameOf(comp.productId)}.` }); setComp({ email: "", productId: "", days: "" }); return load(); }).catch(fail)}>Give access</Button>
          </section>
        </div>
      ) : null}
      <section className="card">
        <DataTable label="Ticket code batches" cellsThead={<><th>Batch</th><th>Unlocks</th><th>Used</th><th>Made</th><th /></>} isEmpty={!batches.length} empty={<p className="p-4 text-body-2 text-t-secondary">No ticket codes made yet.</p>}>
          {batches.map((b) => (
            <DataTableRow key={b.id}>
              <td className="text-body-2">{b.label}{b.revoked ? <> <Chip size="sm" tone="danger">cancelled</Chip></> : null}</td>
              <td className="text-body-2">{nameOf(b.productId)}</td><td className="text-body-2">{b.redeemed} of {b.count}</td><td className="text-body-2 text-t-secondary">{relative(b.createdAt)}</td>
              <td>{canEdit && !b.revoked ? <Button type="button" size="xs" variant="danger-soft" onClick={() => { if (window.confirm(`Cancel every unused code in “${b.label}”? Codes already used keep working.`)) void grants.revokeBatch(client, b.id).then(load); }}>Cancel unused codes</Button> : null}</td>
            </DataTableRow>
          ))}
        </DataTable>
      </section>
      <section className="card">
        <DataTable label="People with access" cellsThead={<><th>Who</th><th>Has</th><th>How</th><th>Until</th><th /></>} isEmpty={!list.length} empty={<p className="p-4 text-body-2 text-t-secondary">Nobody has access this way yet.</p>}>
          {list.map((g) => (
            <DataTableRow key={g.id}>
              <td className="text-body-2">{g.email ?? g.accountId}</td><td className="text-body-2">{nameOf(g.productId)}</td>
              <td className="text-body-2 text-t-secondary">{g.source === "voucher" ? "Ticket code" : g.source === "guest_list" ? "Guest list" : "Free access"}</td>
              <td className="text-body-2 text-t-secondary">{g.expiresAt ? dateTime(g.expiresAt) : "No end"}</td>
              <td>{canEdit ? <Button type="button" size="xs" variant="danger-soft" onClick={() => { if (window.confirm(`Take back ${nameOf(g.productId)} from ${g.email ?? "this person"}? They lose access straight away.`)) void grants.revoke(client, g.id).then(load); }}>Take back</Button> : null}</td>
            </DataTableRow>
          ))}
        </DataTable>
      </section>
    </>
  );
}
